import { X } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Separator } from 'components/ui/separator';
import { Skeleton } from 'components/ui/skeleton';
import {
  IKnowledgeBaseItem,
  KBType,
} from 'features/chat/agent-chat-module/types/kb.service.type';
import { useGetUserById } from 'features/profile/hooks/use-account';
import { UserDetailsData } from 'features/profile/services/accounts-service';

import { formatFullDate, getKbName, getKbTypeLabel } from '../utils';

type KbDocumentInfoProps = {
  kb?: IKnowledgeBaseItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type InfoRowProps = {
  label: string;
  value: string;
  isLoading?: boolean;
};

function InfoRow({ label, value, isLoading }: InfoRowProps) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-1 break-words text-sm text-foreground">
        {isLoading ? <Skeleton className="h-4 w-32" /> : value}
      </dd>
    </div>
  );
}

export function KbDocumentInfo({ kb, open, onOpenChange }: KbDocumentInfoProps) {
  const createdById = kb?.created_by ?? '';
  const { data: createdByUser, isLoading: isLoadingCreatedBy } = useGetUserById(createdById, {
    enabled: open && !!createdById,
  });

  if (!open || !kb) {
    return null;
  }

  const fileExtension =
    kb.kb_type === KBType.FILE && kb.file?.name
      ? kb.file.name.split('.').pop()?.toUpperCase() || '-'
      : '-';

  const user = createdByUser as UserDetailsData | undefined;
  const createdByName = user
    ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email || '-'
    : '-';

  return (
    <div className="flex h-full min-h-0 w-full shrink-0 flex-col xl:w-80 xl:max-w-[25%]">
      <Card className="flex h-full min-h-0 flex-col overflow-hidden border-border shadow-sm">
        <CardHeader className="flex shrink-0 flex-row items-center justify-between space-y-0 pb-3">
          <CardTitle className="text-base font-semibold">Document Info</CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => onOpenChange(false)}
            aria-label="Close info panel"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        <CardContent className="min-h-0 flex-1 space-y-4 overflow-y-auto">
          <dl className="space-y-4">
            <InfoRow label="Name" value={getKbName(kb)} />
            <InfoRow label="Type" value={getKbTypeLabel(kb.kb_type)} />
            {kb.kb_type === KBType.FILE && <InfoRow label="File Type" value={fileExtension} />}
            <InfoRow label="Chunks" value={String(kb.total_chunks ?? 0)} />
            <InfoRow label="Chunk Size" value={String(kb.chunk_size ?? 0)} />
            <InfoRow label="Language" value={kb.language || '-'} />
            <InfoRow label="Created" value={formatFullDate(kb.created_date)} />
            <InfoRow label="Last Updated" value={formatFullDate(kb.last_updated_date)} />
            <InfoRow
              label="Created By"
              value={createdByName}
              isLoading={!!createdById && isLoadingCreatedBy}
            />
            {kb.kb_type === KBType.LINK && kb.link?.url && (
              <InfoRow label="URL" value={kb.link.url} />
            )}
          </dl>
          <Separator />
          <p className="text-xs text-muted-foreground">ID: {kb.id}</p>
        </CardContent>
      </Card>
    </div>
  );
}
